import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Scale } from "lucide-react";
import { motion } from "framer-motion";
import { api, CATEGORIES, CATEGORY_SHORT } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Input } from "@/components/ui/input";

export default function Catalogue() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "Tout";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data.map((c) => c.name))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = new URLSearchParams();
    if (cat !== "Tout") query.set("category", cat);
    if (q) query.set("q", q);
    api.get(`/products?${query.toString()}`)
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cat, q]);

  const selectCat = (c) => {
    const p = new URLSearchParams(params);
    if (c === "Tout") p.delete("cat"); else p.set("cat", c);
    setParams(p);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search.trim()) p.set("q", search.trim()); else p.delete("q");
    setParams(p);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-14" data-testid="catalogue-page">
      <p className="font-mono text-xs uppercase tracking-widest text-ciel">Catalogue</p>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Tous les produits digitaux</h1>

      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {["Tout", ...(cats.length ? cats : CATEGORIES)].map((c) => (
            <button
              key={c}
              data-testid={`filter-${CATEGORY_SHORT[c] || "tout"}`}
              onClick={() => selectCat(c)}
              className={`relative rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                cat === c ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-ciel hover:text-ciel"
              }`}
            >
              {CATEGORY_SHORT[c] || c}
              {cat === c && <motion.span layoutId="cat-pill" className="absolute inset-0 -z-10 rounded-full bg-navy" />}
            </button>
          ))}
          {cat === "Solutions hospitalières proposées" && (
            <Link to="/solutions/comparatif" data-testid="catalogue-comparatif-link" className="inline-flex items-center gap-1.5 rounded-full border border-[#7DC4E8] bg-[#E0F2FE] px-4 py-2 text-xs font-semibold text-[#0369A1] transition-colors duration-200 hover:bg-white">
              <Scale size={13} /> Comparer les solutions
            </Link>
          )}
        </div>
        <form onSubmit={submitSearch} className="relative w-full lg:w-80">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="catalogue-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un produit…"
            className="rounded-full border-slate-300 bg-white pl-10 focus-visible:ring-ciel"
          />
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ciel" />
        </div>
      ) : products.length === 0 ? (
        <p className="py-24 text-center text-slate-500" data-testid="catalogue-empty">Aucun produit ne correspond à votre recherche.</p>
      ) : (
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="catalogue-grid">
          {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      )}
    </div>
  );
}
